import { computed, ref } from 'vue'
import type { EnvironmentName } from '../../configs/types'
import { DEFAULT_APP_DOWNLOAD_URL } from '../../configs/appDownload'
import { api } from '@/api'
import { getDeviceFeature } from '@/utils/deviceFeature'
import { isUCBrowser } from '@/utils/bowserEnv'
import { brand, brandConfig } from '@/config/brands'

export const bannerEligible = ref(false)
// 旧页面 Banner 与顶部 Banner 互斥，阅读器的旧占位也同步移除。
export const bannerShow = computed(() => !brandConfig.appBanner?.topBanner && bannerEligible.value)

/**
 * 是否通过达人分享进入
 * 标识在 useAttribution 中收集归因信息时持久化
 */
function isTalentShare() {
  return localStorage.getItem('IS_TALENT_SHARE') === '1'
}

let pending: Promise<void> | undefined

export function useBannerShow() {
  if (bannerEligible.value || isUCBrowser() || isTalentShare())
    return

  const config = brandConfig.appBanner
  if (!config || (config.hostKeyword && !window.location.hostname.toLowerCase().includes(config.hostKeyword)))
    return
  if (pending)
    return pending

  const request = config.strategy === 'layer-and-ad'
    ? Promise.all([
      api.post(`/member/layer/match?layerConfigIdStr=${import.meta.env.VITE_BIZ_SHOWBANNER_LAYER_ID}`),
      api.get('/member/ad/info'),
    ]).then(([layer, ad]) => !!layer.data && !!ad.data?.displayDirectToApp)
    : api.get('/member/ad/info').then(res => !!res.data?.displayDirectToApp)

  pending = request.then((eligible) => {
    bannerEligible.value = eligible
  }).catch((error) => {
    console.error('获取下载 Banner 配置失败:', error)
  }).finally(() => {
    pending = undefined
  })
  return pending
}

/** 下载地址由当前环境配置提供，未配置的品牌保留历史跳转行为。 */
function getAppCallUrl(target: string) {
  const url = brandConfig.environments[import.meta.env.MODE as EnvironmentName]?.appDownloadUrl
    || DEFAULT_APP_DOWNLOAD_URL
  return url.replace('{target}', encodeURIComponent(target))
}

export function useCallApp() {
  async function callApp(id?: string, index?: number) {
    const token = localStorage.getItem('TOKEN')
    let target
    let copyStr

    if (id && index) {
      target = `/read?id=${id}&index=${Number(index) - 1}`
      copyStr = `#${brand}#${token}#${target}`
    }
    else if (id) {
      target = `/book_detail?id=${id}`
      copyStr = `#${brand}#${token}#${target}`
    }
    else {
      target = ''
      copyStr = `#${brand}#${token}`
    }

    // 剪贴板授权失败或设备信息上报失败，不应阻断下载跳转。
    try {
      await navigator.clipboard.writeText(copyStr)
    }
    catch (err) {
      console.error('复制失败:', err)
    }
    try {
      const deviceFeature = await getDeviceFeature()
      await api.post('/member/deviceFeature', { deviceFeature })
    }
    catch (error) {
      console.error('设备信息上报失败:', error)
    }
    window.location.href = getAppCallUrl(target)
  }

  return {
    callApp,
  }
}
